import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      // Always show the same success state, whether or not the email exists,
      // so this can't be used to check which emails are registered.
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err, 'Something went wrong. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary section="Forgot Password">
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-2 text-center text-2xl font-bold text-gray-900">Forgot your password?</h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Enter your email and we'll send you a link to reset it.
            </p>
          </div>

          <div className="bg-white p-8 shadow rounded-lg">
            {submitted ? (
              <div className="text-center space-y-4">
                <div className="rounded-md bg-green-50 p-4">
                  <p className="text-sm text-green-700">
                    If an account exists for {email}, we've sent a password reset link to it.
                    Check your inbox (and spam folder).
                  </p>
                </div>
                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                  Back to sign in
                </Link>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="rounded-md bg-red-50 p-4" role="alert">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
                <div className="text-center">
                  <Link to="/login" className="text-sm text-blue-600 hover:text-blue-500">
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ForgotPassword;
